import React, { useEffect } from "react";
// Import necessary hooks and utilities
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BASE_URL } from "../../../config";
import {
  MdDateRange,
  MdPhone,
  MdLocationOn,
  MdArrowBack,
  MdCalendarToday,
  MdInfoOutline,
} from "react-icons/md";

// --- Utility: Format Date ---
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// --- Helper Component for consistent display of key info (Moved up for clarity) ---
const InfoPill = ({ icon, title, value }) => (
  <div className="flex flex-col items-start p-4 bg-gray-50 rounded-lg shadow-sm">
    <div className="flex items-center text-Light mb-2">
      {icon}
      <p className="ml-2 text-sm font-medium uppercase tracking-wider text-Dark/70">
        {title}
      </p>
    </div>
    <p className="text-Dark text-lg font-semibold">{value}</p>
  </div>
);

function InitiativeDetail() {
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Receive the initiative object from the navigation state
  const initiative = location.state?.initiative;

  // 2. Handle case where state is missing (e.g., direct navigation)
  useEffect(() => {
    if (!initiative) {
      // Redirect back to the list if no data is found
      navigate("/community", { replace: true });
    }
  }, [initiative, navigate]);

  // Show a loading/redirecting message if data is missing while effect runs
  if (!initiative) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center text-Dark font-semibold">
          <p className="animate-pulse">Loading or redirecting...</p>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${BASE_URL.replace(/\/$/, "")}/initiative-details/${
    initiative.id
  }`;

  // Custom value for End Date: formatted date or "Ongoing"
  const endDateValue = initiative.end_date
    ? formatDate(initiative.end_date)
    : "Ongoing";

  return (
    <div className="min-h-screen font-Poppins bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{initiative.title} Details – CCD Community</title>
        <meta
          name="description"
          content={`Detailed information for the community initiative: ${initiative.title}.`}
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* --- Page Header & Back Button --- */}
      <div className="bg-Dark dark:bg-gray-800 py-12 md:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white/80 hover:text-white transition-colors mb-4 text-sm font-medium"
          >
            <MdArrowBack size={20} className="mr-2" />
            Back to Community Initiatives
          </button>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
            {initiative.title}
          </h1>
          <p className="text-Light/80 mt-2 text-lg">
            Full Details & Event Information
          </p>
        </div>
      </div>

      {/* --- Content Section --- */}
      <div className="max-w-6xl mx-auto sm:p-2 lg:p-8 pt-10">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 md:p-10 border border-gray-100 dark:border-gray-700">
          {/* Initiative Image */}
          {initiative.image_url && (
            <div className="w-full h-64 md:h-96 overflow-hidden rounded-xl mb-8">
              <img
                src={`${BASE_URL}${initiative.image_url}`}
                alt={initiative.title}
                className="w-full h-full object-cover transition duration-500 hover:scale-105"
              />
            </div>
          )}

          {/* Key Information Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-10 pb-6 border-b border-gray-100 dark:border-gray-700">
            <InfoPill
              icon={
                <MdCalendarToday size={20} className="dark:text-gray-300" />
              }
              title="Start Date"
              value={formatDate(initiative.start_date)}
            />
            <InfoPill
              icon={<MdDateRange size={20} className="dark:text-gray-300" />}
              title="End Date"
              value={endDateValue}
            />
            <InfoPill
              icon={<MdLocationOn size={20} className="dark:text-gray-300" />}
              title="Location"
              value={initiative.location || "Community Wide"}
            />
            <InfoPill
              icon={<MdPhone size={20} className="dark:text-gray-300" />}
              title="Contact Info"
              value={initiative.contact_info || "See Community Portal"}
            />
          </div>

          {/* Description */}
          <h2 className="text-2xl font-bold text-Dark dark:text-gray-300 mb-4 border-b pb-2">
            Description
          </h2>
          <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">
            {initiative.description}
          </p>
        </div>
      </div>
    </div>
  );
}

export default InitiativeDetail;
